import moment from 'moment';
import type { HazardPictogramCode } from '@karnott/icons';
import { createAppSlice } from '.';
import {
  addPhytoMixtureProduct,
  createCustomPhytoProduct,
  createPhytoFavoriteProduct,
  createPhytoMixture,
  deleteCustomPhytoProduct,
  deletePhytoFavoriteProduct,
  deletePhytoMixtureProduct,
  getIftReports,
  getPhytoBasicProducts,
  getPhytoFavoriteProducts,
  getPhytoMixtures,
  requestIftReport,
  updateCustomPhytoProduct,
  updatePhytoMixture,
  updatePhytoMixtureProduct,
} from '../api/api';
import { TaskId } from '../api/queries/workOrders';
import { MixtureState, ProductState, UsageIssues, UsageState } from '../constants/phytosConstants';
import { MixtureLine, toCreatePayload, toUpdatePayload } from '../utils/mixtureLine';
import { ItemWithPermissions } from '../utils/permissions';
import { PartialWithRequired } from '../utils/types';
import { ClusterId } from './clusters';
import { IftBilan } from './ift';
import { ParcelId } from './parcels';
import { alerting, success } from './snacks';

type MixtureId = string;
export type ProductId = string;
export type ProductUsageId = string;

export type ProductUsage = {
  id: ProductUsageId;
  product_id: ProductId;
  short_usage_id: string;
  usage_id: string;
  decision_date: string;
  state: UsageState;
  dose: number;
  dose_unit: string;
  nb_max_application?: number;
  recolt_delay: number | null;
  znt_aquatique: number | null;
};

type HazardPictogram = {
  code: HazardPictogramCode;
  label: string;
};

export type Hazard = {
  pictograms: HazardPictogram[];
  has_unclassified_products?: boolean;
};

export type RiskPhrase = {
  code: string;
  label: string;
};

type ReferenceProduct = ItemWithPermissions & {
  id: ProductId;
  name: string;
  aam_reference: string;
  active_substance: string;
  amm: string;
  commercial_type: string;
  cluster_id: undefined;
  first_authorization_date: string;
  formulations: string;
  functions: string[];
  hazard?: Hazard | null;
  is_favorite: boolean;
  product_type: string;
  reentry_delays: string[];
  risk_phrases?: RiskPhrase[];
  secondary_name: string;
  state: ProductState;
  titular: string;
  usages: ProductUsage[];
  withdrawal_date: string;
};

export type CustomProduct = ItemWithPermissions & {
  id: ProductId;
  name: string;
  cluster_id: ClusterId;
  active_substance: string;
  usages: [ProductUsage];
  is_favorite: boolean;
  functions: null;
  reentry_delays: null;
  amm: '';
};

export type MixtureProductUsage = {
  is_custom_product: boolean;
  product_id: ProductId;
  product_name: string;
  product_amm: string;
  product_short_risk: string[];
  product_state: ProductState | '';
  product_usage_id: ProductUsageId;
  product_usage_dose: number;
  product_usage_usage_id: string;
  product_usage_znt: number;
  product_usage_recolt_delay: number;
  product_usage_state: UsageState;
  status: UsageIssues;
  dose: number;
  dose_unit: string;
  function: string;
  product_functions: string[];
  product_reentry_delays: string[];
  unauthorized_with_other_product_ids: ProductId[];
};

type MixtureCreation = {
  cluster_id: ClusterId;
  name: string;
  custom_function?: string;
  product_usages: { product_usage_id: ProductUsageId; dose: number; function?: string }[];
};

export type Mixture = ItemWithPermissions & {
  cluster_id: ClusterId;
  /** Override of the function label. `null` when the label is computed. */
  custom_function?: string | null;
  /** Resolved and translated function label. Read-only, write `custom_function`. */
  function?: string;
  id: MixtureId;
  name: string;
  status: MixtureState;
  product_usages: MixtureProductUsage[];
};

export type Product = ReferenceProduct | CustomProduct;

export type PhytoRegister = {
  count_parcel_passage: number;
  date: string;
  dose_unit: string;
  parcel_area: number;
  parcel_id: ParcelId;
  parcel_name: string;
  product_id: ProductId;
  product_name: string;
  product_usage_id: ProductUsageId;
  quantity_unit: string;
  /** Cluster_id linked to the parcel at the time of the task (client if delegated, else owner) */
  task_cluster_id: ClusterId;
  task_cluster_name: string;
  /** Owner cluster of the task: the dimension filtered by context_cluster_id */
  owner_cluster_id: ClusterId;
  owner_cluster_name: string;
  task_ids: TaskId[];
  total_dose: number;
  total_quantity: number;
  total_worked_area: number;
};

type State = {
  products: Record<ClusterId, Product[]>;
  mixtures: Record<ClusterId, Mixture[]>;
  iftReports: Record<ClusterId, IftBilan>;
};

const initialState: State = {
  products: {},
  mixtures: {},
  iftReports: [],
};

function updateProduct(state: State, clusterId: ClusterId, product: Product) {
  state.products[clusterId] ??= [];
  state.products[clusterId] = state.products[clusterId].filter((p) => p.id !== product.id);
  state.products[clusterId].push(product);
}

const phytoSlice = createAppSlice({
  name: 'phyto',
  initialState,
  reducers: (create) => ({
    fetchBasicProducts: create.asyncThunk(async (name: string, { signal, dispatch }) => {
      const products = (await alerting(() => getPhytoBasicProducts(name, signal), { dispatch })) as Product[];
      return products;
    }),

    // Favorite products
    fetchFavoriteProducts: create.asyncThunk(
      async (clusterId: ClusterId, { dispatch }) => {
        if (!clusterId) {
          return;
        }
        const products = (await alerting(() => getPhytoFavoriteProducts(clusterId), { dispatch })) as Product[];
        return products;
      },
      {
        fulfilled: (state, { payload, meta: { arg: clusterId } }) => {
          if (payload) {
            state.products[clusterId] = payload;
          }
        },
      },
    ),
    addFavoriteProduct: create.asyncThunk(
      async ({ productId, clusterId }: { productId: ProductId; clusterId: ClusterId }, { dispatch }) => {
        const { product } = (await alerting(() => createPhytoFavoriteProduct(productId, clusterId), { dispatch })) as {
          product: Product;
        };
        success('AdminAddProductModal.success_snack', { dispatch });
        return product;
      },
      {
        fulfilled: (
          state,
          {
            payload,
            meta: {
              arg: { clusterId },
            },
          },
        ) => {
          updateProduct(state, clusterId, payload);
        },
      },
    ),
    removeFavoriteProduct: create.asyncThunk(
      async ({ productId, clusterId }: { productId: ProductId; clusterId: ClusterId }, { dispatch }) => {
        await alerting(() => deletePhytoFavoriteProduct(productId, clusterId), { dispatch });
        success('AdminAddProductModal.remove_success_snack', { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { clusterId, productId },
            },
          },
        ) => {
          state.products[clusterId] ??= [];
          state.products[clusterId] = state.products[clusterId].filter((p) => p.id !== productId);
        },
      },
    ),

    // Custom products
    addCustomProduct: create.asyncThunk(async (product: Partial<CustomProduct>, { dispatch }) => {
      const newProduct = (await alerting(() => createCustomPhytoProduct(product), { dispatch })) as CustomProduct;
      return newProduct;
    }),
    updateCustomProduct: create.asyncThunk(
      async (product: PartialWithRequired<CustomProduct, 'id'>, { dispatch }) => {
        const updatedProduct = (await alerting(() => updateCustomPhytoProduct(product.id, product), {
          dispatch,
        })) as CustomProduct;
        success('AdminCustomProductModal.edit_success_snack', { dispatch });
        return updatedProduct;
      },
      {
        fulfilled: (state, { payload }) => {
          updateProduct(state, payload.cluster_id, payload);
        },
      },
    ),
    deleteCustomProduct: create.asyncThunk(
      async (product: PartialWithRequired<CustomProduct, 'id' | 'cluster_id'>, { dispatch }) => {
        await alerting(() => deleteCustomPhytoProduct(product.id), { dispatch });
        success('AdminAddProductModal.remove_success_snack', { dispatch });
        return product;
      },
      {
        fulfilled: (state, { payload }) => {
          const clusterId = payload.cluster_id;
          const productId = payload.id;
          if (clusterId) {
            state.products[clusterId] ??= [];
            state.products[clusterId] = state.products[clusterId].filter((p) => p.id !== productId);
          }
        },
      },
    ),
    // Mixtures
    fetchMixtures: create.asyncThunk(
      async (clusterId: ClusterId, { dispatch }) => {
        if (!clusterId) return;
        const mixtures = (await alerting(() => getPhytoMixtures(clusterId), { dispatch })) as Mixture[];
        return mixtures;
      },
      {
        fulfilled: (state, { payload, meta: { arg: clusterId } }) => {
          if (payload && clusterId) {
            state.mixtures[clusterId] = payload;
          }
        },
      },
    ),
    fetchAllMixtures: create.asyncThunk(
      async (_, { dispatch }) => {
        const mixtures = (await alerting(getPhytoMixtures, { dispatch })) as Mixture[];
        const mixturesByClusterId: Record<ClusterId, Mixture[]> = {};
        mixtures.forEach((mixture) => {
          mixturesByClusterId[mixture.cluster_id] ||= [];
          mixturesByClusterId[mixture.cluster_id]?.push(mixture);
        });
        return mixturesByClusterId;
      },
      {
        fulfilled: (state, { payload }) => {
          state.mixtures = payload;
        },
      },
    ),
    addMixture: create.asyncThunk(
      async ({ noSnack, ...mixture }: MixtureCreation & { noSnack?: boolean }, { dispatch }) => {
        const newMixture = (await alerting(() => createPhytoMixture(mixture), { dispatch })) as Mixture;
        await alerting(
          () =>
            Promise.all([
              dispatch(fetchMixtures(newMixture.cluster_id)),
              // fetch products because product authorizations depend on the mixtures
              dispatch(fetchFavoriteProducts(newMixture.cluster_id)),
            ]),
          { dispatch },
        );
        if (!noSnack) {
          success('AdminMixture.create_success_snack', { dispatch });
        }
        return newMixture;
      },
    ),
    updateMixture: create.asyncThunk(
      async (
        {
          mixtureId,
          payload,
        }: {
          mixtureId: MixtureId;
          payload: {
            clusterId: ClusterId;
            mixtureName?: Mixture['name'];
            customFunction?: string;
            diff: {
              toCreate: MixtureLine[];
              toUpdate: MixtureLine[];
              toDelete: ProductUsageId[];
            };
          };
        },
        { dispatch },
      ) => {
        const promises: Promise<unknown>[] = [];
        const mixtureFields: { name?: Mixture['name']; custom_function?: string } = {};
        if (payload.mixtureName) {
          mixtureFields.name = payload.mixtureName;
        }
        if (payload.customFunction !== undefined) {
          mixtureFields.custom_function = payload.customFunction;
        }
        if (Object.keys(mixtureFields).length) {
          promises.push(updatePhytoMixture(mixtureId, mixtureFields));
        }
        payload.diff.toDelete.filter(Boolean).forEach((productUsageId) => {
          promises.push(deletePhytoMixtureProduct(mixtureId, productUsageId));
        });
        payload.diff.toUpdate.forEach((line) => {
          promises.push(updatePhytoMixtureProduct(mixtureId, line.productUsageId, toUpdatePayload(line)));
        });
        payload.diff.toCreate.forEach((line) => {
          promises.push(addPhytoMixtureProduct(mixtureId, toCreatePayload(line)));
        });
        await alerting(() => Promise.all(promises), { dispatch });
        await alerting(
          () =>
            Promise.all([
              dispatch(fetchMixtures(payload.clusterId)),
              // fetch products because product authorizations depend on the mixtures
              dispatch(fetchFavoriteProducts(payload.clusterId)),
            ]),
          { dispatch },
        );
        success('AdminMixture.edit_success_snack', { dispatch });
      },
    ),
    archiveMixture: create.asyncThunk(
      async (
        {
          mixtureId,
          clusterId,
          isCurrentlyArchived = false,
        }: {
          mixtureId: MixtureId;
          clusterId: ClusterId;
          isCurrentlyArchived?: boolean;
        },
        { dispatch },
      ) => {
        await alerting(() => updatePhytoMixture(mixtureId, { status: isCurrentlyArchived ? 'active' : 'archived' }), {
          dispatch,
        });
        await dispatch(fetchMixtures(clusterId));
        success('AdminMixture.edit_success_snack', { dispatch });
      },
    ),

    // IFT
    fetchIftReports: create.asyncThunk(
      async (_, { dispatch }) => {
        const iftReports = (await alerting(getIftReports, { dispatch })) as IftBilan[];
        return iftReports;
      },
      {
        fulfilled: (state, { payload }) => {
          state.iftReports = payload.reduce((p: Record<ClusterId, IftBilan>, c) => {
            if (
              !p[c.cluster_id] ||
              (p[c.cluster_id] && moment(p[c.cluster_id]!.created_at).isBefore(moment(c.created_at)))
            ) {
              p[c.cluster_id] = c;
            }
            return p;
          }, {});
        },
      },
    ),
    addIftReport: create.asyncThunk(
      async (
        {
          clusterId,
          fromDate,
          toDate,
        }: {
          clusterId: ClusterId;
          fromDate: moment.Moment;
          toDate: moment.Moment;
        },
        { dispatch },
      ) => {
        const params = new URLSearchParams({ from_date: fromDate.format(), to_date: toDate.format() });
        const report = (await alerting(() => requestIftReport(clusterId, params), { dispatch })) as IftBilan;
        return report;
      },
      {
        fulfilled: (state, { payload }) => {
          state.iftReports[payload.cluster_id] = payload;
        },
      },
    ),
  }),
  selectors: {
    selectMixturesByClusterId: (state) => state.mixtures,
    selectIftReports: (state) => state.iftReports,
    selectFavoriteProductsByClusterId: (state) => state.products,
    selectPhytoProducts: (state) => state.products,
  },
});

export const {
  fetchBasicProducts,

  addFavoriteProduct,
  fetchFavoriteProducts,
  removeFavoriteProduct,

  addCustomProduct,
  updateCustomProduct,
  deleteCustomProduct,

  fetchMixtures,
  fetchAllMixtures,
  addMixture,
  updateMixture,
  archiveMixture,

  fetchIftReports,
  addIftReport,
} = phytoSlice.actions;

export const { selectFavoriteProductsByClusterId, selectIftReports, selectMixturesByClusterId, selectPhytoProducts } =
  phytoSlice.selectors;

export default phytoSlice.reducer;
